import React, { useState, useEffect, useCallback } from 'react'
import { api } from '../services/authService'
import toast from 'react-hot-toast'
import {
  BoltIcon,
  PlusIcon,
  PlayIcon,
  PauseIcon,
  PencilIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon,
  UserPlusIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  ArrowTrendingUpIcon,
  PauseCircleIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  FunnelIcon,
  ChartBarIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  EyeIcon,
  CogIcon,
  ListBulletIcon,
  RocketLaunchIcon,
  SignalIcon
} from '@heroicons/react/24/outline'

const TRIGGER_ICON_MAP = {
  novo_lead: UserPlusIcon,
  lead_sem_resposta: ClockIcon,
  proposta_enviada: DocumentTextIcon,
  contrato_vencendo: ExclamationTriangleIcon,
  follow_up_agendado: CalendarDaysIcon,
  score_atingido: ArrowTrendingUpIcon,
  lead_inativo: PauseCircleIcon,
  manual: PlayIcon
}

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  active: { label: 'Ativa', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
  paused: { label: 'Pausada', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' }
}

const EXEC_STATUS_CONFIG = {
  started: { label: 'Iniciada', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  processing: { label: 'Processando', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' },
  completed: { label: 'Concluida', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
  failed: { label: 'Falhou', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' }
}

const EMPTY_ACTION = { type: 'whatsapp', template_slug: '', delay_minutes: 0, minutes: 60 }

const MarketingAutomationPage = () => {
  const [activeTab, setActiveTab] = useState('automacoes')
  const [loading, setLoading] = useState(false)

  // Automacoes state
  const [automations, setAutomations] = useState([])
  const [triggerTypes, setTriggerTypes] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: '',
    trigger_config: {},
    actions: [{ ...EMPTY_ACTION }]
  })
  const [selectedTrigger, setSelectedTrigger] = useState(null)

  // Execucoes state
  const [executions, setExecutions] = useState([])
  const [execFilter, setExecFilter] = useState('')
  const [expandedExecId, setExpandedExecId] = useState(null)

  // Metricas state
  const [metrics, setMetrics] = useState(null)

  // ------- Data loading -------

  const loadAutomations = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.get('/api/marketing/automation')
      setAutomations(response.data?.data || response.data || [])
    } catch (error) {
      console.error('Erro ao carregar automacoes:', error)
      toast.error('Erro ao carregar automacoes')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadTriggerTypes = useCallback(async () => {
    try {
      const response = await api.get('/api/marketing/automation/trigger-types')
      setTriggerTypes(response.data?.data || response.data || [])
    } catch (error) {
      console.error('Erro ao carregar tipos de gatilho:', error)
    }
  }, [])

  const loadExecutions = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      if (execFilter) params.status = execFilter
      const response = await api.get('/api/marketing/automation/executions/list', { params })
      setExecutions(response.data?.data || response.data || [])
    } catch (error) {
      console.error('Erro ao carregar execucoes:', error)
      toast.error('Erro ao carregar execucoes')
    } finally {
      setLoading(false)
    }
  }, [execFilter])

  const loadMetrics = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.get('/api/marketing/automation/metrics/overview')
      setMetrics(response.data?.data || response.data || null)
    } catch (error) {
      console.error('Erro ao carregar metricas:', error)
      toast.error('Erro ao carregar metricas')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'automacoes') {
      loadAutomations()
      loadTriggerTypes()
    } else if (activeTab === 'execucoes') {
      loadExecutions()
    } else if (activeTab === 'metricas') {
      loadMetrics()
    }
  }, [activeTab, loadAutomations, loadTriggerTypes, loadExecutions, loadMetrics])

  // ------- Automation CRUD -------

  const handleActivate = async (id) => {
    try {
      await api.post(`/api/marketing/automation/${id}/activate`)
      toast.success('Automacao ativada com sucesso')
      loadAutomations()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao ativar automacao')
    }
  }

  const handlePause = async (id) => {
    try {
      await api.post(`/api/marketing/automation/${id}/pause`)
      toast.success('Automacao pausada')
      loadAutomations()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao pausar automacao')
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta automacao?')) return
    try {
      await api.delete(`/api/marketing/automation/${id}`)
      toast.success('Automacao excluida')
      loadAutomations()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir automacao')
    }
  }

  const openCreateModal = () => {
    setEditingId(null)
    setFormData({
      name: '',
      description: '',
      trigger_type: '',
      trigger_config: {},
      actions: [{ ...EMPTY_ACTION }]
    })
    setSelectedTrigger(null)
    setShowModal(true)
  }

  const openEditModal = (automation) => {
    setEditingId(automation.id)
    const trigger = triggerTypes.find(t => t.type === automation.trigger_type) || null
    setSelectedTrigger(trigger)
    setFormData({
      name: automation.name || '',
      description: automation.description || '',
      trigger_type: automation.trigger_type || '',
      trigger_config: automation.trigger_config || {},
      actions: automation.actions?.length
        ? automation.actions.map(a => ({
            type: a.type || 'whatsapp',
            template_slug: a.template_slug || '',
            delay_minutes: a.delay_minutes ?? 0,
            minutes: a.minutes ?? 60
          }))
        : [{ ...EMPTY_ACTION }]
    })
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Informe o nome da automacao')
      return
    }
    if (!formData.trigger_type) {
      toast.error('Selecione um tipo de gatilho')
      return
    }
    if (formData.actions.length === 0) {
      toast.error('Adicione pelo menos uma acao')
      return
    }

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        trigger_type: formData.trigger_type,
        trigger_config: formData.trigger_config,
        actions: formData.actions.map((a, idx) => ({
          order: idx + 1,
          type: a.type,
          template_slug: a.type !== 'wait' ? a.template_slug : null,
          delay_minutes: Number(a.delay_minutes) || 0,
          minutes: a.type === 'wait' ? Number(a.minutes) || 60 : null
        }))
      }

      if (editingId) {
        await api.put(`/api/marketing/automation/${editingId}`, payload)
        toast.success('Automacao atualizada com sucesso')
      } else {
        await api.post('/api/marketing/automation', payload)
        toast.success('Automacao criada com sucesso')
      }
      setShowModal(false)
      loadAutomations()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar automacao')
    }
  }

  // ------- Action helpers -------

  const addAction = () => {
    setFormData(prev => ({
      ...prev,
      actions: [...prev.actions, { ...EMPTY_ACTION }]
    }))
  }

  const removeAction = (index) => {
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }))
  }

  const updateAction = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.actions]
      updated[index] = { ...updated[index], [field]: value }
      return { ...prev, actions: updated }
    })
  }

  const moveAction = (index, direction) => {
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= formData.actions.length) return
    setFormData(prev => {
      const updated = [...prev.actions]
      const temp = updated[index]
      updated[index] = updated[newIndex]
      updated[newIndex] = temp
      return { ...prev, actions: updated }
    })
  }

  const selectTriggerType = (trigger) => {
    setSelectedTrigger(trigger)
    setFormData(prev => ({
      ...prev,
      trigger_type: trigger.type,
      trigger_config: {}
    }))
  }

  const updateTriggerConfig = (field, value) => {
    setFormData(prev => ({
      ...prev,
      trigger_config: { ...prev.trigger_config, [field]: value }
    }))
  }

  // ------- Formatting helpers -------

  const formatMinutes = (mins) => {
    const m = Number(mins)
    if (m <= 0) return 'Imediato'
    if (m < 60) return `${m} min`
    if (m < 1440) {
      const h = Math.floor(m / 60)
      const remainder = m % 60
      return remainder > 0 ? `${h}h ${remainder}min` : `${h} hora${h > 1 ? 's' : ''}`
    }
    const d = Math.floor(m / 1440)
    const remainingH = Math.floor((m % 1440) / 60)
    return remainingH > 0 ? `${d} dia${d > 1 ? 's' : ''} e ${remainingH}h` : `${d} dia${d > 1 ? 's' : ''}`
  }

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-'
    try {
      return new Date(dateStr).toLocaleString('pt-BR')
    } catch {
      return dateStr
    }
  }

  const getTriggerIcon = (triggerType) => {
    return TRIGGER_ICON_MAP[triggerType] || BoltIcon
  }

  const getTriggerLabel = (triggerType) => {
    const found = triggerTypes.find(t => t.type === triggerType)
    return found?.name || triggerType
  }

  // ------- Tab definitions -------

  const tabs = [
    { key: 'automacoes', label: 'Automacoes', icon: BoltIcon },
    { key: 'execucoes', label: 'Execucoes', icon: ListBulletIcon },
    { key: 'metricas', label: 'Metricas', icon: ChartBarIcon }
  ]

  // =============================================
  // RENDER: Automacoes Tab
  // =============================================

  const renderAutomationsTab = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Automacoes de Marketing
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Configure fluxos automatizados de comunicacao com leads e clientes
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          Nova Automacao
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-gray-500 dark:text-gray-400">Carregando...</span>
        </div>
      ) : automations.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <BoltIcon className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-600 dark:text-gray-300 mb-2">
            Nenhuma automacao cadastrada
          </h3>
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">
            Crie sua primeira automacao para enviar mensagens automaticas aos seus leads
          </p>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Criar Automacao
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {automations.map(auto => {
            const TriggerIconComp = getTriggerIcon(auto.trigger_type)
            const statusCfg = STATUS_CONFIG[auto.status] || STATUS_CONFIG.draft
            return (
              <div
                key={auto.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                      <TriggerIconComp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
                        {auto.name}
                      </h3>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
                        {statusCfg.label}
                      </span>
                    </div>
                  </div>
                </div>

                {auto.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
                    {auto.description}
                  </p>
                )}

                <div className="flex items-center gap-4 mb-4 text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <TriggerIconComp className="h-3.5 w-3.5" />
                    <span>{getTriggerLabel(auto.trigger_type)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-gray-800 dark:text-gray-100">
                      {Number(auto.total_executions || 0).toLocaleString('pt-BR')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Execucoes</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-green-600 dark:text-green-400">
                      {Number(auto.total_conversions || 0).toLocaleString('pt-BR')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Conversoes</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
                  {auto.status !== 'active' && (
                    <button
                      onClick={() => handleActivate(auto.id)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 dark:bg-green-900/30 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                      title="Ativar"
                    >
                      <PlayIcon className="h-3.5 w-3.5" />
                      Ativar
                    </button>
                  )}
                  {auto.status === 'active' && (
                    <button
                      onClick={() => handlePause(auto.id)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-yellow-700 bg-yellow-50 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/50 transition-colors"
                      title="Pausar"
                    >
                      <PauseIcon className="h-3.5 w-3.5" />
                      Pausar
                    </button>
                  )}
                  <button
                    onClick={() => openEditModal(auto)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                    title="Editar"
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(auto.id)}
                    className="flex items-center justify-center p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    title="Excluir"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )

  // =============================================
  // RENDER: Modal de Criacao/Edicao
  // =============================================

  const renderModal = () => {
    if (!showModal) return null

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              {editingId ? 'Editar Automacao' : 'Nova Automacao'}
            </h2>
            <button
              onClick={() => setShowModal(false)}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Nome e Descricao */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome da Automacao *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Follow-up de novo lead"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Descricao
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva o objetivo desta automacao..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            {/* Selecao de Trigger */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Gatilho (Trigger) *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {triggerTypes.map(trigger => {
                  const TIcon = TRIGGER_ICON_MAP[trigger.type] || BoltIcon
                  const isSelected = formData.trigger_type === trigger.type
                  return (
                    <button
                      key={trigger.type}
                      onClick={() => selectTriggerType(trigger)}
                      className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all text-center ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 bg-white dark:bg-gray-700'
                      }`}
                    >
                      <TIcon className={`h-6 w-6 ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                      <span className={`text-xs font-medium ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400'}`}>
                        {trigger.name}
                      </span>
                    </button>
                  )
                })}
              </div>

              {selectedTrigger?.description && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-2 rounded-lg">
                  {selectedTrigger.description}
                </p>
              )}
            </div>

            {/* Campos dinamicos do trigger_config */}
            {selectedTrigger?.config_fields?.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Configuracao do Gatilho
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl">
                  {selectedTrigger.config_fields.map(field => (
                    <div key={field.name || field}>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        {field.label || field.name || field}
                      </label>
                      {field.type === 'select' ? (
                        <select
                          value={formData.trigger_config[field.name || field] || ''}
                          onChange={(e) => updateTriggerConfig(field.name || field, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Selecione...</option>
                          {(field.options || []).map(opt => (
                            <option key={opt.value || opt} value={opt.value || opt}>
                              {opt.label || opt}
                            </option>
                          ))}
                        </select>
                      ) : field.type === 'number' ? (
                        <input
                          type="number"
                          value={formData.trigger_config[field.name || field] || ''}
                          onChange={(e) => updateTriggerConfig(field.name || field, e.target.value)}
                          placeholder={field.placeholder || ''}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <input
                          type="text"
                          value={formData.trigger_config[field.name || field] || ''}
                          onChange={(e) => updateTriggerConfig(field.name || field, e.target.value)}
                          placeholder={field.placeholder || ''}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions Builder */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Acoes da Automacao
                </label>
                <button
                  onClick={addAction}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  Adicionar Acao
                </button>
              </div>

              <div className="space-y-3">
                {formData.actions.map((action, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-200 dark:border-gray-600"
                  >
                    {/* Order indicator */}
                    <div className="flex flex-col items-center gap-1 pt-1">
                      <span className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-bold">
                        {idx + 1}
                      </span>
                      <button
                        onClick={() => moveAction(idx, -1)}
                        disabled={idx === 0}
                        className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Mover para cima"
                      >
                        <ChevronUpIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => moveAction(idx, 1)}
                        disabled={idx === formData.actions.length - 1}
                        className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Mover para baixo"
                      >
                        <ChevronDownIcon className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Action fields */}
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Tipo</label>
                        <select
                          value={action.type}
                          onChange={(e) => updateAction(idx, 'type', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="whatsapp">WhatsApp</option>
                          <option value="email">Email</option>
                          <option value="wait">Aguardar</option>
                        </select>
                      </div>

                      {action.type === 'whatsapp' && (
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Template WhatsApp
                          </label>
                          <input
                            type="text"
                            value={action.template_slug}
                            onChange={(e) => updateAction(idx, 'template_slug', e.target.value)}
                            placeholder="slug do template"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}

                      {action.type === 'email' && (
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Template Email
                          </label>
                          <input
                            type="text"
                            value={action.template_slug}
                            onChange={(e) => updateAction(idx, 'template_slug', e.target.value)}
                            placeholder="slug do template"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}

                      {action.type === 'wait' && (
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Tempo de Espera (minutos)
                          </label>
                          <input
                            type="number"
                            value={action.minutes}
                            onChange={(e) => updateAction(idx, 'minutes', e.target.value)}
                            min="1"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 block">
                            = {formatMinutes(action.minutes)}
                          </span>
                        </div>
                      )}

                      {action.type !== 'wait' && (
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Atraso (minutos)
                          </label>
                          <input
                            type="number"
                            value={action.delay_minutes}
                            onChange={(e) => updateAction(idx, 'delay_minutes', e.target.value)}
                            min="0"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 block">
                            {action.delay_minutes > 0 ? formatMinutes(action.delay_minutes) : '0 = imediato'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Remove button */}
                    <button
                      onClick={() => removeAction(idx)}
                      disabled={formData.actions.length <= 1}
                      className="p-1.5 text-red-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed mt-5"
                      title="Remover acao"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {editingId ? 'Salvar Alteracoes' : 'Criar Automacao'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // =============================================
  // RENDER: Execucoes Tab
  // =============================================

  const renderExecutionsTab = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Historico de Execucoes
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Acompanhe todas as execucoes das automacoes em tempo real
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FunnelIcon className="h-4 w-4 text-gray-400" />
          <select
            value={execFilter}
            onChange={(e) => setExecFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos os status</option>
            <option value="started">Iniciada</option>
            <option value="processing">Processando</option>
            <option value="completed">Concluida</option>
            <option value="failed">Falhou</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-gray-500 dark:text-gray-400">Carregando...</span>
        </div>
      ) : executions.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <ListBulletIcon className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-600 dark:text-gray-300 mb-2">
            Nenhuma execucao encontrada
          </h3>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            As execucoes aparecerao aqui quando as automacoes forem disparadas
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Automacao
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Lead / Empresa
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Gatilho
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Acoes
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Inicio
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Conclusao
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Detalhes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {executions.map(exec => {
                  const execStatusCfg = EXEC_STATUS_CONFIG[exec.status] || EXEC_STATUS_CONFIG.started
                  const isExpanded = expandedExecId === exec.id
                  const actionsExecuted = exec.actions_executed || []
                  const actionsCount = Array.isArray(actionsExecuted) ? actionsExecuted.length : 0

                  return (
                    <React.Fragment key={exec.id}>
                      <tr className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                        <td className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-gray-100">
                          {exec.automation_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {exec.company_name || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                            {React.createElement(getTriggerIcon(exec.trigger_type), { className: 'h-3.5 w-3.5' })}
                            {getTriggerLabel(exec.trigger_type)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${execStatusCfg.color}`}>
                            {execStatusCfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-300">
                          {actionsCount}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                          {formatDateTime(exec.started_at)}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                          {formatDateTime(exec.completed_at)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setExpandedExecId(isExpanded ? null : exec.id)}
                            className="p-1 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                            title="Ver detalhes"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>

                      {/* Expanded row - timeline */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} className="px-4 py-4 bg-gray-50 dark:bg-gray-750">
                            <div className="ml-4">
                              <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-3">
                                Timeline de Acoes Executadas
                              </h4>
                              {actionsCount === 0 ? (
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                  Nenhuma acao executada ainda
                                </p>
                              ) : (
                                <div className="space-y-2">
                                  {(Array.isArray(actionsExecuted) ? actionsExecuted : []).map((act, aIdx) => (
                                    <div
                                      key={aIdx}
                                      className="flex items-center gap-3 text-xs"
                                    >
                                      <div className={`flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center ${
                                        act.status === 'completed' || act.status === 'sent'
                                          ? 'bg-green-100 dark:bg-green-900/50'
                                          : act.status === 'failed'
                                          ? 'bg-red-100 dark:bg-red-900/50'
                                          : 'bg-gray-100 dark:bg-gray-600'
                                      }`}>
                                        {act.status === 'completed' || act.status === 'sent' ? (
                                          <CheckCircleIcon className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                                        ) : act.status === 'failed' ? (
                                          <ExclamationCircleIcon className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                                        ) : (
                                          <ClockIcon className="h-3.5 w-3.5 text-gray-400" />
                                        )}
                                      </div>
                                      <div className="flex-1">
                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                          {act.type === 'whatsapp' ? 'WhatsApp' : act.type === 'email' ? 'Email' : act.type === 'wait' ? 'Aguardar' : act.type}
                                        </span>
                                        {act.template_slug && (
                                          <span className="ml-2 text-gray-500 dark:text-gray-400">
                                            ({act.template_slug})
                                          </span>
                                        )}
                                      </div>
                                      <span className="text-gray-400 dark:text-gray-500">
                                        {formatDateTime(act.executed_at)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )

  // =============================================
  // RENDER: Metricas Tab
  // =============================================

  const renderMetricsTab = () => {
    const kpis = [
      {
        label: 'Total Automacoes',
        value: metrics?.total_automations ?? 0,
        icon: BoltIcon,
        color: 'text-blue-600 dark:text-blue-400',
        bg: 'bg-blue-50 dark:bg-blue-900/30'
      },
      {
        label: 'Ativas',
        value: metrics?.active_automations ?? 0,
        icon: RocketLaunchIcon,
        color: 'text-green-600 dark:text-green-400',
        bg: 'bg-green-50 dark:bg-green-900/30'
      },
      {
        label: 'Execucoes (30d)',
        value: metrics?.total_executions_30d ?? 0,
        icon: SignalIcon,
        color: 'text-purple-600 dark:text-purple-400',
        bg: 'bg-purple-50 dark:bg-purple-900/30'
      },
      {
        label: 'Fila Pendente',
        value: metrics?.pending_queue ?? 0,
        icon: ClockIcon,
        color: 'text-orange-600 dark:text-orange-400',
        bg: 'bg-orange-50 dark:bg-orange-900/30'
      }
    ]

    const statusBreakdown = metrics?.executions_by_status || {}
    const statusEntries = Object.entries(statusBreakdown)
    const maxStatusVal = Math.max(1, ...statusEntries.map(([, v]) => Number(v)))

    const conversionRate = metrics?.conversion_rate != null
      ? Number(metrics.conversion_rate)
      : null

    return (
      <div>
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Metricas de Automacao
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Visao geral do desempenho das automacoes nos ultimos 30 dias
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-3 text-gray-500 dark:text-gray-400">Carregando...</span>
          </div>
        ) : !metrics ? (
          <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <ChartBarIcon className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-600 dark:text-gray-300">
              Sem dados disponiveis
            </h3>
          </div>
        ) : (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {kpis.map((kpi, idx) => {
                const KIcon = kpi.icon
                return (
                  <div
                    key={idx}
                    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {kpi.label}
                      </span>
                      <div className={`p-2 rounded-lg ${kpi.bg}`}>
                        <KIcon className={`h-5 w-5 ${kpi.color}`} />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                      {Number(kpi.value).toLocaleString('pt-BR')}
                    </p>
                  </div>
                )
              })}
            </div>

            {/* Conversion Rate */}
            {conversionRate !== null && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Taxa de Conversao
                </h3>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(100, conversionRate)}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-lg font-bold text-gray-800 dark:text-gray-100 min-w-[60px] text-right">
                    {conversionRate.toFixed(1)}%
                  </span>
                </div>
              </div>
            )}

            {/* Executions by Status (bar chart) */}
            {statusEntries.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  Execucoes por Status
                </h3>
                <div className="space-y-3">
                  {statusEntries.map(([status, count]) => {
                    const cfg = EXEC_STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-700' }
                    const pct = (Number(count) / maxStatusVal) * 100
                    const barColors = {
                      started: 'bg-blue-500',
                      processing: 'bg-yellow-500',
                      completed: 'bg-green-500',
                      failed: 'bg-red-500'
                    }

                    return (
                      <div key={status} className="flex items-center gap-3">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 w-28">
                          {cfg.label}
                        </span>
                        <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${barColors[status] || 'bg-gray-400'} rounded-full transition-all duration-500`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold text-gray-700 dark:text-gray-300 min-w-[40px] text-right">
                          {Number(count).toLocaleString('pt-BR')}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // =============================================
  // MAIN RENDER
  // =============================================

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <BoltIcon className="h-7 w-7 text-blue-600 dark:text-blue-400" />
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            Automacao de Marketing
          </h1>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 ml-10">
          Configure fluxos automatizados de comunicacao B2B com leads e clientes
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 bg-white dark:bg-gray-800 rounded-xl p-1 border border-gray-200 dark:border-gray-700 w-fit">
        {tabs.map(tab => {
          const TabIcon = tab.icon
          const isActive = activeTab === tab.key
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <TabIcon className="h-4 w-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'automacoes' && renderAutomationsTab()}
      {activeTab === 'execucoes' && renderExecutionsTab()}
      {activeTab === 'metricas' && renderMetricsTab()}

      {/* Modal */}
      {renderModal()}
    </div>
  )
}

export default MarketingAutomationPage
